/**
 * 导入预览「点字卡 → 浮窗选读音」的**状态机** + 「重复出现的位置」的筛选
 * （2026-10-19 第二版，用户实测后收紧）。
 *
 * ## 用户原话（验收标准）
 *
 * > "你现在预览之后，如果出现多音字，你的方法是在**下面单独列一块**给字选择读音。
 * >  目前体验是：**点击那个字之后，就弹出一个浮窗**给他来选择，就不用跑到下面去。
 * >  像我现在上面选了一个字，还在下面去看；**如果没有看到的话，还不知道下面有选择的呢**。
 * >  **最好就地或者弹窗，有个窗来选择**。"
 *
 * > "我看到下面还有一个空，有一块是『重复出现的位置，一个字』。我觉得，**如果是多音字的话
 * >  这边就没必要显示了**，因为多音字你在上面每个字的**底色已经用玫瑰色显示出来了**。
 * >  **这里可以用来显示那些不是多音字、还有重复出现的字。如果没有，那就不用显示。**"
 *
 * ## 为什么单独抽一个文件
 *
 * `apps/web` 的用例只跑纯函数（`vitest.config.ts`：`environment: 'node'`，
 * 刻意不装 jsdom / @testing-library）。"点卡会不会开浮窗""选完会不会关窗并重新预览"
 * "取消会不会偷偷改东西"这类**只会在交互里发生**的事，只能把状态机抽出来测 ——
 * 与 `lib/skipUndo.ts` 同一套做法。
 *
 * ## 状态机只有四件事
 *
 * | 事件 | 状态 | 副作用 |
 * | --- | --- | --- |
 * | `open`（点字卡） | `selected` = 那个位置 | **无**（只是开窗，不请求、不改音） |
 * | `pick`（点一个读音，**旧的直接生效路径**） | 立刻关窗 | `pickReading`（父组件据此重新预览） |
 * | `confirm`（点「✅ 确认就是「X」」） | 立刻关窗 | `pickReading`（读音按传进来的值写） |
 * | `cancel`（取消 / Esc / 遮罩 / ✕） | 关窗 | **无** |
 *
 * ⚠️ **2026-10-20 第八轮起，浮窗不再派发 `pick`**：用户明确要求"点读音只是选中，
 *    再点确认按钮才确认并关窗"（见 `ImportReadingModal.tsx`）。所以实际入口
 *    统一是 `confirm` —— 传进来的 `reading` 就是浮窗里**选中的那个音**
 *    （同音 = 只确认；换了音 = 改读音 + 确认）。
 *    `pick` 保留在状态机里（与 `confirm` 对父组件是同一个副作用），
 *    ⛔ 不要再把它接回浮窗 —— 那正是用户本轮要修掉的交互。
 *
 * ⚠️ `confirm` 的 `reading` 由浮窗给（= 当前选中的音），reducer 这里一个字都不改。
 */
import { availableReadings } from '@fht/shared';
import type { RepeatedPositionHint } from '@fht/shared';

/** 浮窗要处理的位置（行号 + 这一行第几个字，1 开始）。 */
export interface ImportPreviewTarget {
  line: number;
  indexInLine: number;
}

export interface ImportPreviewPickerState {
  /** 浮窗打开的那个位置；`null` = 关着。 */
  selected: ImportPreviewTarget | null;
}

export const INITIAL_IMPORT_PREVIEW_PICKER: ImportPreviewPickerState = { selected: null };

export type ImportPreviewPickerEvent =
  | { type: 'open'; line: number; indexInLine: number }
  /** 旧的"点读音直接生效"路径（⛔ 浮窗已不再派发，见文件头）。 */
  | { type: 'pick'; reading: string }
  /** 「✅ 确认就是「X」」：把**浮窗里选中的音**写进这个位置（同音 = 只确认）。 */
  | { type: 'confirm'; reading: string }
  | { type: 'cancel' };

export type ImportPreviewPickerEffect =
  | { type: 'none' }
  | { type: 'pickReading'; line: number; indexInLine: number; reading: string };

export interface ImportPreviewPickerStep {
  state: ImportPreviewPickerState;
  /** 调用方**只**对 `pickReading` 做事（重新预览）；其余一律不请求。 */
  effect: ImportPreviewPickerEffect;
}

/**
 * 一个事件 → 下一个状态 + 一个副作用。
 *
 * ⚠️ 没有打开浮窗时收到 `pick`（关窗动画期间的迟到事件 / 双击的第二下）：
 *    返回**同一个 state 对象**与 `none` —— 不会凭空选一个读音，也不会重新预览。
 */
export function reduceImportPreviewPicker(
  state: ImportPreviewPickerState,
  event: ImportPreviewPickerEvent,
): ImportPreviewPickerStep {
  switch (event.type) {
    case 'open':
      return {
        state: { selected: { line: event.line, indexInLine: event.indexInLine } },
        effect: { type: 'none' },
      };

    case 'pick':
    case 'confirm': {
      const target = state.selected;
      const reading = event.reading.trim();
      if (target === null || reading === '') {
        return { state, effect: { type: 'none' } };
      }
      return {
        state: INITIAL_IMPORT_PREVIEW_PICKER,
        effect: {
          type: 'pickReading',
          line: target.line,
          indexInLine: target.indexInLine,
          reading,
        },
      };
    }

    case 'cancel':
      return { state: INITIAL_IMPORT_PREVIEW_PICKER, effect: { type: 'none' } };
  }
}

/**
 * 「重复出现的位置」里**该显示**的那些：重复出现 **且技术上不是多音字**。
 *
 * 判据（用户倾向的那条，这里定下来）：
 *   · **重复**：后端已经算好的 `readings.repeated`（同一份粘贴里出现 ≥2 次的字）；
 *   · **不是多音字**：`availableReadings(charText).length >= 2`（**技术层**读音个数，
 *     与「重复出现的多音字但还没确认标注」用的是同一份内置拼音表）。
 *
 * 为什么不用"用户有没有标音"：标了音只说明"这个位置的读音确认过了"，
 * **不能说明这个字该出现两次**。多音字重复是正常的（一个字多个读音），
 * 而技术上只有一个读音的字重复出现，才可能是**录错了**（用户要排查的正是这个）。
 * `availableReadings` 只有 1 个（或内置表没有它 = 0 个）⇒ 留在清单里。
 *
 * ⚠️ 只是**提示**：不参与 `readings.ok`、不拦保存（后端那三道校验一个字都没动）。
 */
export function nonPolyphonicRepeatedHints(
  repeated: readonly RepeatedPositionHint[],
): RepeatedPositionHint[] {
  return repeated.filter((hint) => availableReadings(hint.charText).length < 2);
}
